import { useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '../api/client';
import type { CategoryNode } from '../api/types';
import { categoryNodesById, pathIdsTo } from '../lib/category-tree';
import {
  categoryIdsAfterAdd,
  categoryNameProblem,
  cleanCategoryName,
  findSiblingByName,
  MAX_CATEGORY_NAME_LENGTH,
  PendingCategoryError,
  withoutInstitutionLevel,
} from '../lib/new-category';
import { Button } from './button';
import { CategoryPicker } from './category-picker';
import { Input } from './input';

export interface AddCategoryAndFileInput {
  documentId: string;
  /** The category the new one goes inside (never the top level). */
  parentId: string;
  /** Already cleaned (cleanCategoryName) and validated. */
  name: string;
  /** An approved sibling with the same normalized name: file into it, create nothing. */
  existingId: string | null;
  /** Every approved category id the room lists (to spot a reused pending one). */
  knownIds: ReadonlySet<string>;
  /** The document's category set once the new category's id is known. */
  categoryIdsFor: (newId: string) => string[];
}

export interface AddCategoryAndFileResult {
  categoryId: string;
  /** False when an existing category of that name was used (no duplicate made). */
  created: boolean;
}

/** The app's "create the category, then file the document there" mutation (a
 * react-query mutation in both apps; this is the part the form reads). */
export interface AddCategoryMutation {
  mutate: (
    input: AddCategoryAndFileInput,
    options?: { onSuccess?: (result: AddCategoryAndFileResult) => void },
  ) => void;
  isPending: boolean;
  error: unknown;
}

export interface AddCategoryFormProps {
  documentId: string;
  /** The app's create-and-file action (see {@link AddCategoryMutation}). */
  add: AddCategoryMutation;
  categories: CategoryNode[];
  /** id → "A / B / C" over the room's tree. */
  paths: ReadonlyMap<string, string>;
  parentOf: ReadonlyMap<string, string | null>;
  /** The document's current category ids. */
  filedIds: readonly string[];
  /** The deepest category it is filed in — the default parent — or null. */
  deepestFiledId: string | null;
  /** Another action in the panel is in flight. */
  disabled: boolean;
  /** Reports this form's own save in flight, so the panel disables its other actions. */
  onBusyChange: (busy: boolean) => void;
  onCancel: () => void;
  /** The document is filed: `filedPath` is "A / B / New", `created` false = an existing one was used. */
  onDone: (result: { filedPath: string; created: boolean }) => void;
}

/**
 * "Add a new category…" in the review panel (owner ask 2026-10-12): a category
 * the room's checklist did not think of — most often the missing third level —
 * created inside a parent and the document filed into it in ONE action
 * (the app's create-and-file mutation: the existing folder import, then the category PATCH
 * that records the person's decision). The parent defaults to the deepest
 * category the document is filed in and can be changed with the same picker as
 * "Choose a different branch". A same-named sibling is filed into, never
 * duplicated; a full account number is refused (the last 4 is suggested).
 * Mounted only while open, so the closed panel stays presentational.
 */
export function AddCategoryForm({
  documentId,
  add,
  categories,
  paths,
  parentOf,
  filedIds,
  deepestFiledId,
  disabled,
  onBusyChange,
  onCancel,
  onDone,
}: AddCategoryFormProps) {
  const nameId = useId();
  const hintId = useId();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string | null>(deepestFiledId);
  const [pickingParent, setPickingParent] = useState(deepestFiledId === null);
  const knownIds = useMemo(() => new Set(categoryNodesById(categories).keys()), [categories]);

  useEffect(() => {
    onBusyChange(add.isPending);
  }, [add.isPending, onBusyChange]);

  const clean = cleanCategoryName(name);
  const problem = categoryNameProblem(name);
  const sibling = parentId ? findSiblingByName(categories, parentId, clean) : null;
  const parentPath = parentId ? (paths.get(parentId) ?? null) : null;
  const parentName = parentPath ? parentPath.split(' / ').at(-1) : null;
  const saving = add.isPending;
  const canSave = !disabled && !saving && parentId !== null && problem === null;

  const error = add.error
    ? add.error instanceof PendingCategoryError
      ? add.error.message
      : errorMessage(add.error)
    : null;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSave || parentId === null || parentPath === null) return;
    const parentPathIds = withoutInstitutionLevel(categories, pathIdsTo(parentId, parentOf));
    add.mutate(
      {
        documentId,
        parentId,
        name: clean,
        existingId: sibling?.id ?? null,
        knownIds,
        categoryIdsFor: (newId) =>
          categoryIdsAfterAdd({ filedIds, deepestFiledId, parentPathIds, newId }),
      },
      {
        onSuccess: (result) =>
          onDone({
            filedPath: `${parentPath} / ${sibling?.name ?? clean}`,
            created: result.created,
          }),
      },
    );
  };

  let hint: ReactNode;
  if (problem?.kind === 'account_number') {
    hint = (
      <p id={hintId} className="text-xs text-destructive" role="alert">
        That looks like a full account number. Use only the last 4 digits — for example “
        {problem.suggestion}”.{' '}
        <button
          type="button"
          className="font-semibold text-primary underline-offset-2 hover:underline"
          onClick={() => setName(problem.suggestion)}
        >
          Use “{problem.suggestion}”
        </button>
      </p>
    );
  } else if (problem?.kind === 'slash') {
    hint = (
      <p id={hintId} className="text-xs text-destructive" role="alert">
        A name can&apos;t contain “/” — it separates the levels of a category.
      </p>
    );
  } else if (problem?.kind === 'too_long') {
    hint = (
      <p id={hintId} className="text-xs text-destructive" role="alert">
        Keep the name under {MAX_CATEGORY_NAME_LENGTH} characters.
      </p>
    );
  } else if (sibling) {
    hint = (
      <p id={hintId} className="text-xs text-muted-foreground" role="status">
        “{sibling.name}” already exists under {parentName}. The document will be filed there — no
        duplicate is made.
      </p>
    );
  } else {
    hint = (
      <p id={hintId} className="text-xs text-muted-foreground">
        {parentName
          ? `It gets the same default audience as “${parentName}”. For an account, use only the last 4 digits.`
          : 'Pick the category it goes inside.'}
      </p>
    );
  }

  return (
    <form
      className="space-y-3 rounded border p-3"
      aria-label="Add a new category"
      onSubmit={submit}
      noValidate
    >
      <div>
        <p className="mb-1 text-xs font-medium text-muted-foreground">Inside</p>
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 font-medium">{parentPath ?? 'No category chosen'}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPickingParent((v) => !v)}
            aria-expanded={pickingParent}
            disabled={saving}
          >
            {pickingParent ? 'Done' : 'Change…'}
          </Button>
        </div>
        {pickingParent ? (
          <div
            className="mt-2 max-h-[35vh] overflow-y-auto rounded border p-1"
            role="group"
            aria-label="Choose the category it goes inside"
          >
            <CategoryPicker
              categories={categories}
              selected={parentId ? new Set([parentId]) : new Set()}
              onToggle={(id) => setParentId((prev) => (prev === id ? null : id))}
              disabled={saving}
            />
          </div>
        ) : null}
      </div>
      <div className="space-y-1.5">
        <label htmlFor={nameId} className="text-xs font-medium text-muted-foreground">
          New category name
        </label>
        <Input
          id={nameId}
          value={name}
          maxLength={MAX_CATEGORY_NAME_LENGTH + 20}
          placeholder="e.g. Frank's AMEX 1005"
          autoFocus
          autoComplete="off"
          aria-invalid={problem !== null && problem.kind !== 'empty'}
          aria-describedby={hintId}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
        />
        {hint}
      </div>
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSave}>
          {saving ? 'Filing…' : sibling ? 'File in existing category' : 'Add and file here'}
        </Button>
      </div>
    </form>
  );
}
